import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getTheme, tokensToCssVars } from '@evi/skins';
import { getPortfolio, publicPortfolioPath } from '@/lib/partnerQuotes';
import jobs from '../partner-jobs/PartnerJobs.module.css';
import styles from './Quotes.module.css';

/**
 * A Sales Partner's portfolio, for anyone with the link while the partner has it on: the business and its launched
 * installs (city, kind of site, chargers, launch month, finished photos). Light by default, like the quote page.
 */
export function PortfolioPage({ token }: { token: string }) {
  const q = useQuery({ queryKey: ['partner-portfolio', token], queryFn: () => getPortfolio(token), retry: false, refetchOnWindowFocus: false });
  const [mode, setMode] = useState<'light' | 'dark'>('light');
  const vars = useMemo(() => Object.fromEntries(Object.entries(tokensToCssVars(getTheme('default').variants[mode].tokens)).filter(([, v]) => v !== '')), [mode]);
  useEffect(() => { document.title = q.data ? `${q.data.partner.name}: EV charging installs` : 'EV charging installs'; }, [q.data]);
  const shell = (children: React.ReactNode) => <div className={styles.customer} style={vars as React.CSSProperties} data-mode={mode}>{children}</div>;
  if (q.error) return shell(<main className={styles.customerMain}><h1 className={styles.hero}>Not available</h1>
    <p className={jobs.lead}>This link does not open a portfolio. Ask the contractor for a current link.</p></main>);
  if (!q.data) return shell(<main className={styles.customerMain}><p className={jobs.message}>Loading.</p></main>);
  const { partner, installs } = q.data;
  const month = (m: string) => new Date(`${m}-15T12:00:00Z`).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
  return shell(<>
    <header className={styles.bar}><div className={styles.barInner}>
      <div className={styles.brand}>
        {partner.has_logo && <img className={styles.logo} src={publicPortfolioPath(token, '/logo')} alt={`${partner.name} logo`} />}
        <div><div className={jobs.strong} style={{ fontSize: 18 }}>{partner.name}</div>{partner.licence && <div className={jobs.sub}>Licensed electrical contractor · {partner.licence}</div>}</div>
      </div>
      <div className={styles.network}>Charging network by<strong>EV <span>INITIATIVE</span></strong></div>
    </div></header>
    <main className={styles.customerMain}>
      <div className={jobs.cardHead}>
        <div><div className={jobs.kicker}>EV charging installs</div><h1 className={styles.hero}>{partner.name}</h1>
          <div className={jobs.lead} style={{ fontSize: 16 }}>{[partner.phone, partner.email].filter(Boolean).join(' · ')}</div></div>
        <button type="button" className={styles.modeToggle} onClick={() => setMode(mode === 'light' ? 'dark' : 'light')}>{mode === 'light' ? 'Dark' : 'Light'}</button>
      </div>
      {!installs.length && <p className={jobs.hint}>Installs appear here as they launch.</p>}
      {installs.map((x, i) => <section key={i} className={jobs.card}>
        <div className={jobs.cardHead}><h2 style={{ margin: 0, fontSize: 20 }}>{x.site} · {x.city}</h2>
          <span className={jobs.hint}>{x.chargers} charger{x.chargers === 1 ? '' : 's'} · live since {month(x.launched)}</span></div>
        {x.photo_ids.length > 0 && <div className={styles.portfolioPhotos}>{x.photo_ids.map(p =>
          <img key={p} src={publicPortfolioPath(token, `/media/${p}`)} alt={`Finished install in ${x.city}`} loading="lazy" />)}</div>}
      </section>)}
    </main>
  </>);
}
